import { usePostHogCapture } from '@app/core/integrations/posthog/usePostHogCapture';
import { useCallback, useMemo } from 'react';
import { stationsEnum } from './helpers';

/** Values from {@link stationsEnum} passed through the stations scope toggle. */
export type YoutubeStatsStationsScope = (typeof stationsEnum)[keyof typeof stationsEnum];

/** Labels used for graph time windows in date range selector events. */
export type YoutubeStatsRangeLabel = string;

/**
 * Centralizes PostHog events for the stream audit YouTube stats chart.
 * Exposes semantic actions so callers avoid duplicating event names and payload keys.
 */
export function useYoutubeStatsCapture() {
  const capture = usePostHogCapture({ domain: 'youtube_stats' });

  const stationsScopeChanged = useCallback(
    ({ scope }: { scope: YoutubeStatsStationsScope }) => {
      capture('stations_scope_changed', { scope });
    },
    [capture],
  );

  const dateRangeChanged = useCallback(
    ({ range_label }: { range_label: YoutubeStatsRangeLabel }) => {
      capture('date_range_changed', { range_label });
    },
    [capture],
  );

  const stationFilterApplied = useCallback(
    ({ selected_channel_count }: { selected_channel_count: number }) => {
      capture('station_filter_applied', { selected_channel_count });
    },
    [capture],
  );

  return useMemo(
    () => ({
      stationsScopeChanged,
      dateRangeChanged,
      stationFilterApplied,
    }),
    [stationsScopeChanged, dateRangeChanged, stationFilterApplied],
  );
}
